import React, { useRef, useState, useEffect, useCallback } from "react";
import p5 from "p5";
import { useWindowHeight, useWindowWidth } from "@react-hook/window-size";
import { isMobile } from "react-device-detect";
import { useSelector, useDispatch } from "react-redux";
import { PageWrapper } from "./pageWrapper";
import { createPrefixOfResource, getLanguageInfo } from "./../../common";
import { getFontSize, getFontClassName } from "./../../styleFunctions";
import { InfoTextDiv } from "./others";
import languages from "./../../data/languages.json";
import localization from "./../../data/localization.json";
import KiaSignatureLight from "./../../fonts/KiaSignatureLight.ttf";

// ============================================================================
// Constants
// ============================================================================
const COLORS = {
  BACKGROUND: [5, 20, 31],
  WHITE: [255, 255, 255],
  WHITE_TRANSPARENT: [255, 255, 255, 70],
};

const LAYOUT = {
  ASPECT_RATIO: 360 / 640,
  LOGO_TOP: 0.05,
  LOGO_HEIGHT: 0.04,
  TITLE_TOP: 0.1,
  TITLE_SIZE: 0.035,
  BRAND_TOP: 0.35,
  BRAND_SIZE: 0.045,
  CAR_TOP: 0.4,
  CAR_HEIGHT: 0.3,
  PROGRESS_CENTER_Y: 0.81,
  PROGRESS_SIZE: 0.14,
  PROGRESS_TEXT_SIZE: 0.034,
  PROGRESS_STROKE_DIVISOR: 200,
  PROGRESS_STROKE_MIN: 3,
  LANG_BUTTON_HEIGHT: 0.067,
  LANG_BUTTON_GAP: 0.075,
  LOADING_TEXT_SIZE_RATIO: 0.017,
  LOADING_TEXT_BOTTOM: 0.025,
};

const TIMING = {
  FRAME_RATE: 30,
  SLIDE_IN_END: 30,
  HOLD_END: 90,
  CYCLE_LENGTH: 120,
  MIDDLE_FUNCTION_DELAY: 2000,
  COMPLETE_FUNCTION_DELAY: 250,
};

// ============================================================================
// Helper Functions
// ============================================================================
const calculateBackgroundDimensions = (windowWidth, windowHeight) => {
  if (windowWidth / windowHeight > LAYOUT.ASPECT_RATIO) {
    return { topMargin: 0, backHeight: windowHeight };
  }
  const backHeight = (windowWidth * 640) / 360;
  return { topMargin: (windowHeight - backHeight) / 2, backHeight };
};

const getLanguageOffset = (languageCount, backHeight) => {
  if (languageCount > 2) {
    return (languageCount - 2) * backHeight * LAYOUT.LANG_BUTTON_GAP;
  }
  return backHeight * LAYOUT.LANG_BUTTON_GAP * 0.7;
};

const calculateCarXPosition = (slideTime, windowWidth) => {
  if (slideTime <= TIMING.SLIDE_IN_END) {
    return (
      ((TIMING.SLIDE_IN_END - slideTime) / TIMING.SLIDE_IN_END + 0.5) *
      windowWidth
    );
  }
  if (slideTime <= TIMING.HOLD_END) {
    return windowWidth / 2;
  }
  return (
    ((TIMING.HOLD_END - slideTime) / TIMING.SLIDE_IN_END + 0.5) * windowWidth
  );
};

const getLoadingText = (language) => {
  const langId = language ?? languages[0]?.id;
  return localization["loading_txt_0"][langId];
};

const getStoredWindowDimensions = (p) => {
  const storedWidth = window.localStorage.getItem("window_width");
  const storedHeight = window.localStorage.getItem("window_height");

  if (storedWidth === null || storedHeight === null) {
    window.localStorage.setItem("window_width", p.windowWidth);
    window.localStorage.setItem("window_height", p.windowHeight);
    return { width: p.windowWidth, height: p.windowHeight };
  }

  return { width: Number(storedWidth), height: Number(storedHeight) };
};

const isPointInRect = (px, py, x, y, width, height) => {
  return px >= x && px < x + width && py >= y && py < y + height;
};

const calculateImageDimensions = (img, targetHeight) => {
  const aspectRatio = img.width / img.height;
  return {
    width: targetHeight * aspectRatio,
    height: targetHeight,
  };
};

// ============================================================================
// P5 Sketch Creator
// ============================================================================
const createLoadingSketch =
  ({
    cars,
    autoComplete,
    noLoading,
    realLoad,
    onMiddle,
    onComplete,
    onLogoStyleChange,
    onCarIndexChange,
    onLoadingTextPositionChange,
  }) =>
  (p) => {
    // State
    let logoImg = null;
    let kiaLight = null;
    let p5Languages = [];
    let p5Cars = [];

    let pWindowWidth = 0;
    let pWindowHeight = 0;
    let topMargin = 0;
    let backHeight = 0;

    let slideTime = 0;
    let currentCarIndex = 0;
    let loadingPercent = noLoading ? 100 : 0;

    let resourcesLoaded = false;
    let completeFunctionCalled = false;
    let languagePressed = false;

    // Dimension calculations
    const updateDimensions = () => {
      const dims = calculateBackgroundDimensions(pWindowWidth, pWindowHeight);
      topMargin = dims.topMargin;
      backHeight = dims.backHeight;
    };

    const getLanguageYOffset = () =>
      getLanguageOffset(p5Languages.length, backHeight);

    const getProgressCenterY = () => {
      return (
        topMargin + backHeight * LAYOUT.PROGRESS_CENTER_Y - getLanguageYOffset()
      );
    };

    // Drawing functions
    const drawBackground = () => {
      p.background(...COLORS.BACKGROUND);
    };

    const drawLogo = () => {
      if (!logoImg) return;

      const dims = calculateImageDimensions(
        logoImg,
        backHeight * LAYOUT.LOGO_HEIGHT,
      );
      p.image(
        logoImg,
        pWindowWidth / 2 - dims.width / 2,
        topMargin + backHeight * LAYOUT.LOGO_TOP,
        dims.width,
        dims.height,
      );
    };

    const drawCar = () => {
      const car = p5Cars[currentCarIndex];
      if (!car?.car) return;

      const carX = calculateCarXPosition(slideTime, pWindowWidth);
      const dims = calculateImageDimensions(
        car.car,
        backHeight * LAYOUT.CAR_HEIGHT,
      );

      p.image(
        car.car,
        carX - dims.width / 2,
        topMargin + backHeight * LAYOUT.CAR_TOP - getLanguageYOffset(),
        dims.width,
        dims.height,
      );
    };

    const updateLogoStyle = () => {
      const car = p5Cars[currentCarIndex];
      if (!car?.brand) return;

      const carX = calculateCarXPosition(slideTime, pWindowWidth);
      onLogoStyleChange({
        left: carX - pWindowWidth / 2,
        right: pWindowWidth - carX - pWindowWidth / 2,
        top: topMargin + backHeight * LAYOUT.BRAND_TOP - getLanguageYOffset(),
        fontSize: backHeight * LAYOUT.BRAND_SIZE,
      });
    };

    const drawLoadingProgress = () => {
      const centerX = pWindowWidth / 2;
      const centerY = getProgressCenterY();
      const size = backHeight * LAYOUT.PROGRESS_SIZE;
      const strokeWeight = Math.max(
        backHeight / LAYOUT.PROGRESS_STROKE_DIVISOR,
        LAYOUT.PROGRESS_STROKE_MIN,
      );

      // Background circle
      p.strokeWeight(strokeWeight);
      p.stroke(...COLORS.WHITE_TRANSPARENT);
      p.noFill();
      p.circle(centerX, centerY, size);

      // Progress arc
      p.stroke(...COLORS.WHITE);
      p.arc(
        centerX,
        centerY,
        size,
        size,
        -p.HALF_PI,
        (2 * p.PI * loadingPercent) / 100 - p.HALF_PI,
      );

      // Percentage text
      p.strokeWeight(1);
      p.stroke(...COLORS.WHITE);
      p.fill(...COLORS.WHITE);
      p.textSize(backHeight * LAYOUT.PROGRESS_TEXT_SIZE);
      p.textAlign(p.CENTER, p.CENTER);
      p.text(
        `${loadingPercent.toFixed(0)} %`,
        centerX,
        centerY + backHeight * 0.002,
      );
    };

    const getLanguageButtonBounds = (index) => {
      const lang = p5Languages[index];
      if (!lang?.unselect) return null;

      const dims = calculateImageDimensions(
        lang.unselect,
        backHeight * LAYOUT.LANG_BUTTON_HEIGHT,
      );
      const x = pWindowWidth / 2 - dims.width / 2;
      const y =
        getProgressCenterY() +
        (index - p5Languages.length / 2) * backHeight * LAYOUT.LANG_BUTTON_GAP;

      return { x, y, width: dims.width, height: dims.height };
    };

    const drawLanguageButtons = () => {
      const currentLang = window.localStorage.getItem("lang_id");

      p5Languages.forEach((lang, index) => {
        if (!lang.unselect || !lang.select) return;

        const bounds = getLanguageButtonBounds(index);
        if (!bounds) return;

        const isSelected = lang.id === currentLang;
        const img = isSelected ? lang.select : lang.unselect;

        p.image(img, bounds.x, bounds.y, bounds.width, bounds.height);

        // Update cursor on hover
        if (
          isPointInRect(
            p.mouseX,
            p.mouseY,
            bounds.x,
            bounds.y,
            bounds.width,
            bounds.height,
          )
        ) {
          p.cursor("pointer");
        }
      });
    };

    const handleLoadingComplete = () => {
      const sceneLoaded = window.localStorage.getItem("sceneLoaded");
      const isReady = noLoading || !realLoad || sceneLoaded === "yes";

      if (!isReady) return;

      if (autoComplete && !completeFunctionCalled) {
        completeFunctionCalled = true;
        setTimeout(
          () => onComplete(localStorage.getItem("lang_id")),
          TIMING.COMPLETE_FUNCTION_DELAY,
        );
        return;
      }

      if (!autoComplete) {
        if (languages.length < 2) {
          if (!completeFunctionCalled) {
            completeFunctionCalled = true;
            onComplete(languages[0].id);
          }
        } else {
          drawLanguageButtons();
        }
      }
    };

    const updateAnimationState = () => {
      // Don't advance if single car and already centered
      if (p5Cars.length === 1 && slideTime >= TIMING.SLIDE_IN_END) return;

      slideTime++;

      if (slideTime >= TIMING.CYCLE_LENGTH) {
        slideTime = 0;
        currentCarIndex = (currentCarIndex + 1) % p5Cars.length;
        onCarIndexChange(currentCarIndex);
      }
    };

    const updateLoadingProgress = () => {
      if (realLoad) {
        loadingPercent =
          Number(window.localStorage.getItem("engineLoadProgress")) * 100;
      } else if (loadingPercent < 100) {
        loadingPercent += 1 + Math.random() * 2;
      }
    };

    // P5 lifecycle methods
    p.preload = () => {
      // Load logo
      p.loadImage(
        `${createPrefixOfResource()}/static/UI/img_kia_showroom_mark.png`,
        (img) => {
          logoImg = img;
        },
      );

      // Load language buttons (only if multiple languages and not auto-complete)
      if (!autoComplete && languages.length >= 2) {
        languages.forEach((lang, index) => {
          p5Languages.push({ id: lang.id, select: null, unselect: null });
          p.loadImage(`${createPrefixOfResource()}${lang.selectSrc}`, (img) => {
            p5Languages[index].select = img;
          });
          p.loadImage(
            `${createPrefixOfResource()}${lang.unselectSrc}`,
            (img) => {
              p5Languages[index].unselect = img;
            },
          );
        });
      }
    };

    p.setup = () => {
      const dims = getStoredWindowDimensions(p);
      pWindowWidth = dims.width;
      pWindowHeight = dims.height;

      onLoadingTextPositionChange(pWindowHeight);
      p.createCanvas(pWindowWidth, pWindowHeight);
      p.frameRate(TIMING.FRAME_RATE);
    };

    p.draw = () => {
      // Load resources on first frame
      if (!resourcesLoaded) {
        cars.forEach((car, index) => {
          p5Cars.push({ car: null, brand: car.brandHTML });
          p.loadImage(`${createPrefixOfResource()}${car.carSrc}`, (img) => {
            p5Cars[index].car = img;
          });
        });
        p.loadFont(KiaSignatureLight, (font) => {
          kiaLight = font;
        });

        resourcesLoaded = true;
        setTimeout(onMiddle, TIMING.MIDDLE_FUNCTION_DELAY);
      }

      updateDimensions();
      p.cursor("default");

      // Draw scene
      drawBackground();
      drawLogo();
      updateLogoStyle();
      drawCar();

      // Draw progress or handle completion
      if (loadingPercent < 100) {
        drawLoadingProgress();
      } else {
        handleLoadingComplete();
      }

      // Update state
      updateAnimationState();
      updateLoadingProgress();
    };

    p.windowResized = () => {
      pWindowWidth = p.windowWidth;
      pWindowHeight = p.windowHeight;
      p.resizeCanvas(pWindowWidth, pWindowHeight + 360);
      onLoadingTextPositionChange(pWindowHeight);
    };

    p.mousePressed = () => {
      if (loadingPercent < 100) return;
      if (languages.length < 2) return;
      if (languagePressed) return;

      p5Languages.forEach((lang, index) => {
        const bounds = getLanguageButtonBounds(index);
        if (!bounds) return;

        if (
          isPointInRect(
            p.mouseX,
            p.mouseY,
            bounds.x,
            bounds.y,
            bounds.width,
            bounds.height,
          )
        ) {
          languagePressed = true;
          onComplete(lang.id);
        }
      });
    };
  };

// ============================================================================
// Custom Hooks
// ============================================================================
const useP5Sketch = (containerRef, sketchCreator) => {
  useEffect(() => {
    const p5Instance = new p5(sketchCreator, containerRef.current);
    return () => p5Instance.remove();
  }, []);
};

const useFirstLoadDispatch = () => {
  const dispatch = useDispatch();
  const firstLoad = useSelector(({ firstLoadReducer }) => firstLoadReducer);

  useEffect(() => {
    if (!firstLoad) {
      dispatch({ type: "FIRST_LOADED" });
    }
  }, [dispatch, firstLoad]);
};

// ============================================================================
// Sub-Components
// ============================================================================
const BrandOverlay = ({ logoStyle, car, language }) => {
  if (!logoStyle) return null;

  return (
    <div
      style={{
        ...logoStyle,
        position: "fixed",
        zIndex: 101,
        color: "white",
        textAlign: "center",
        verticalAlign: "middle",
        overflow: "visible",
      }}
      className={getFontClassName("bold", language)}
      dangerouslySetInnerHTML={{ __html: car.brandHTML }}
    />
  );
};

const TitleOverlay = ({ windowWidth, windowHeight, language }) => {
  const { topMargin, backHeight } = calculateBackgroundDimensions(
    windowWidth,
    windowHeight,
  );
  const isWideAspect = windowWidth / windowHeight > LAYOUT.ASPECT_RATIO;
  let topPosition = isWideAspect
    ? windowHeight * LAYOUT.TITLE_TOP
    : topMargin + backHeight * LAYOUT.TITLE_TOP;

  return (
    <div
      className={getFontClassName("light", language)}
      style={{
        position: "fixed",
        textAlign: "center",
        zIndex: 101,
        fontSize: backHeight * LAYOUT.TITLE_SIZE,
        left: 10,
        right: 10,
        top: topPosition,
        color: "#fff",
      }}
    >
      {language !== null
        ? localization["loading_title_0"][language]
        : "Virtual Experience"}
    </div>
  );
};

const MobileLoadingText = ({ loadingTextPosition, windowWidth, language }) => {
  if (!loadingTextPosition) return null;

  const { backHeight } = calculateBackgroundDimensions(
    windowWidth,
    loadingTextPosition,
  );
  const isWideAspect = windowWidth / loadingTextPosition > LAYOUT.ASPECT_RATIO;
  const bottomPosition = isWideAspect
    ? loadingTextPosition * LAYOUT.LOADING_TEXT_BOTTOM
    : backHeight * LAYOUT.LOADING_TEXT_BOTTOM +
      (loadingTextPosition - backHeight) / 2;

  return (
    <div
      className={getFontClassName("light", language)}
      style={{
        position: "fixed",
        left: 10,
        right: 10,
        bottom: bottomPosition,
        zIndex: 101,
        color: "white",
        fontSize: getFontSize(
          loadingTextPosition * LAYOUT.LOADING_TEXT_SIZE_RATIO,
          language,
        ),
        textAlign: "center",
        verticalAlign: "middle",
        direction: getLanguageInfo(language).rtl ? "rtl" : "ltr",
      }}
    >
      {getLoadingText(language)}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================
export const LoadingScreen = ({
  cars = [],
  autoComplete = false,
  noLoading = false,
  middleFunction = () => {},
  completeFunction = () => {},
  realLoad = false,
}) => {
  const p5Ref = useRef(null);
  const windowHeight = useWindowHeight();
  const windowWidth = useWindowWidth();

  const language = useSelector(
    ({ languageReducer }) => languageReducer.language,
  );

  const [logoStyle, setLogoStyle] = useState(null);
  const [carIdx, setCarIdx] = useState(0);
  const [loadingTextPosition, setLoadingTextPosition] = useState(null);

  useFirstLoadDispatch();

  const sketchCreator = useCallback(
    () =>
      createLoadingSketch({
        cars,
        autoComplete,
        noLoading,
        realLoad,
        onMiddle: middleFunction,
        onComplete: completeFunction,
        onLogoStyleChange: setLogoStyle,
        onCarIndexChange: setCarIdx,
        onLoadingTextPositionChange: setLoadingTextPosition,
      }),
    [cars, autoComplete, noLoading, realLoad, middleFunction, completeFunction],
  );

  useEffect(() => {
    window.completeFunction = () => completeFunction(languages[0].id);
  }, [completeFunction]);

  useP5Sketch(p5Ref, sketchCreator());

  const carId = cars.length > 1 ? "default" : cars[0]?.id;

  return (
    <PageWrapper carId={carId}>
      <div
        ref={p5Ref}
        className="vrloadingReadyButton"
        style={{
          position: "fixed",
          zIndex: 100,
          width: "100vw",
          left: 0,
          top: 0,
          bottom: -360,
          overflow: "hidden",
          backgroundColor: `rgb(${COLORS.BACKGROUND.join(", ")})`,
        }}
      >
        <div id="p5_loading" />
      </div>

      <BrandOverlay
        logoStyle={logoStyle}
        car={cars[carIdx]}
        language={language}
      />

      <TitleOverlay
        windowWidth={windowWidth}
        windowHeight={windowHeight}
        language={language}
      />

      {isMobile ? (
        <MobileLoadingText
          loadingTextPosition={loadingTextPosition}
          windowWidth={windowWidth}
          language={language}
        />
      ) : (
        <InfoTextDiv color="#ffffff" zIndex={105} />
      )}
    </PageWrapper>
  );
};
